import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, isStaff, tenantId, role, subscribed, isPlatformOwner, refreshSubscription } = useAuth();
  const [needsOnboarding, setNeedsOnboarding] = useState<boolean | null>(null);
  const [tenantSuspended, setTenantSuspended] = useState<boolean | null>(null);

  useEffect(() => {
    if (!tenantId || !isStaff || role === "owner") {
      setNeedsOnboarding(false);
      return;
    }

    if (localStorage.getItem(`master_onboarding_done_${user?.id}`) === "true") {
      setNeedsOnboarding(false);
      return;
    }

    const checkTenant = async () => {
      const { data } = await supabase
        .from("tenants")
        .select("name")
        .eq("id", tenantId)
        .maybeSingle();

      if (data?.name?.endsWith(" Academy")) {
        setNeedsOnboarding(true);
      } else {
        localStorage.setItem(`master_onboarding_done_${user?.id}`, "true");
        setNeedsOnboarding(false);
      }
    };
    checkTenant();
  }, [tenantId, isStaff, role, user?.id]);

  // Check if tenant is suspended
  useEffect(() => {
    if (!tenantId || !isStaff || isPlatformOwner) {
      setTenantSuspended(false);
      return;
    }

    const checkTenantStatus = async () => {
      const { data } = await supabase.rpc("get_tenant_status", { _tenant_id: tenantId });
      setTenantSuspended(data === "suspended" || data === "cancelled");
    };
    checkTenantStatus();
  }, [tenantId, isStaff, isPlatformOwner]);

  if (loading || needsOnboarding === null || tenantSuspended === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  if (!isStaff) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="text-center space-y-3">
          <h1 className="text-xl font-bold text-foreground">Acesso Restrito</h1>
          <p className="text-sm text-muted-foreground">
            Esta área é exclusiva para professores e administradores.
          </p>
          <p className="text-sm text-muted-foreground">
            Se você é aluno, acesse{" "}
            <a href="/aluno/login" className="text-primary underline">a área do aluno</a>.
          </p>
        </div>
      </div>
    );
  }

  // Show subscription expired screen
  // Only block masters (who control subscription) or when tenant is explicitly suspended
  const isMasterRole = role === "master";
  const isNutricionista = role === "nutricionista";
  if (tenantSuspended && !isPlatformOwner && !isNutricionista) {
    return <SubscriptionExpiredScreen />;
  }
  if (isMasterRole && !subscribed && !isPlatformOwner) {
    return <SubscriptionExpiredScreen />;
  }

  if (needsOnboarding) {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
}

function SubscriptionExpiredScreen() {
  const { refreshSubscription, signOut } = useAuth();
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const { toast } = useToast();

  const handleReactivate = async (priceId: string, tierKey: string) => {
    setCheckoutLoading(tierKey);
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: { priceId },
      });
      if (error) throw error;
      if (data?.url) window.location.href = data.url;
    } catch {
      toast({ title: "Erro", description: "Não foi possível iniciar o checkout.", variant: "destructive" });
    } finally {
      setCheckoutLoading(null);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="max-w-md w-full text-center space-y-6">
        <div className="mx-auto h-16 w-16 rounded-2xl bg-destructive/10 flex items-center justify-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-destructive" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>

        <div>
          <h1 className="text-2xl font-bold text-foreground">Assinatura Expirada</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Sua assinatura foi cancelada ou expirou. Para continuar usando a plataforma, reative seu plano.
          </p>
        </div>

        <div className="space-y-2">
          {([
            ["starter", "price_1T9FY9DRGBnYut7cUBmjBPOZ", "Starter", "R$49,90/mês"],
            ["pro", "price_1T9FYXDRGBnYut7cO3zYiPGX", "Pro", "R$99,90/mês"],
            ["academy", "price_1T9FYuDRGBnYut7cm4cZu8m7", "Academy", "R$199,90/mês"],
            ["enterprise", "price_1T9FZGDRGBnYut7cp673Nbll", "Enterprise", "R$499,90/mês"],
          ] as const).map(([key, priceId, name, price]) => (
            <button
              key={key}
              onClick={() => handleReactivate(priceId, key)}
              disabled={!!checkoutLoading}
              className="w-full flex items-center justify-between rounded-xl border border-border/50 bg-card px-4 py-3 hover:bg-muted/50 transition-colors disabled:opacity-50"
            >
              <div className="text-left">
                <p className="text-sm font-semibold text-foreground">{name}</p>
                <p className="text-xs text-muted-foreground">{price}</p>
              </div>
              {checkoutLoading === key ? (
                <Loader2 className="h-4 w-4 animate-spin text-primary" />
              ) : (
                <span className="text-xs font-medium text-primary">Reativar →</span>
              )}
            </button>
          ))}
        </div>

        <div className="flex gap-3 justify-center pt-2">
          <button
            onClick={refreshSubscription}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors underline"
          >
            Já reativei, verificar
          </button>
          <button
            onClick={signOut}
            className="text-xs text-muted-foreground hover:text-destructive transition-colors underline"
          >
            Sair
          </button>
        </div>
      </div>
    </div>
  );
}
